import { useEffect, useState } from "react";

/**
 * useIsDesktop — SSR-safe media query hook за Tailwind `md:` breakpoint (≥768px).
 *
 * Използва се от Dashboard и други layouts за conditional rendering вместо
 * CSS `hidden md:block`/`md:hidden` подхода, който държи и двете поддървета
 * mounted в React → двойни API calls. Single-mount варианти намаляват мрежата
 * и ускоряват TTI.
 *
 * initialIsDesktop=true → на server-render или first paint приемаме desktop,
 * за да не "мига" мобилният layout при normal users. След mount реалната
 * matchMedia стойност се прилага моментално.
 */
export default function useIsDesktop(minWidth = 768) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    // Първоначален sync (в случай, че initial state е бил различен)
    setIsDesktop(mq.matches);
    // addEventListener е modern API; addListener е legacy fallback за Safari <14
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [minWidth]);

  return isDesktop;
}
